import { useCallback, useEffect, useRef, useState } from 'react';
import { Eye, FileDown, FileText, Loader2, Pencil } from 'lucide-react';
import { marked } from 'marked';
import { Button } from '../ui/Button';
import { MarkdownPreview } from '../ui/MarkdownPreview';
import { ZoomablePane } from '../ui/ZoomablePane';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { DraftAiSideChat, type DraftAiSideChatMessage } from './DraftAiSideChat';

marked.setOptions({ gfm: true, breaks: true });

function synergyPlanFilenameStem(): string {
  return `ai-synergy-plan-${new Date().toISOString().slice(0, 10)}`;
}

function downloadTextFile(filename: string, text: string, mime: string) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function markdownToHtmlForPrint(markdown: string): string {
  const out = marked.parse(markdown, { async: false });
  return typeof out === 'string' ? out : '';
}

async function renderMermaidInPrintRoot(root: HTMLElement): Promise<void> {
  const mermaid = (await import('mermaid')).default;
  mermaid.initialize({ startOnLoad: false, theme: 'neutral', securityLevel: 'strict' });
  root.querySelectorAll('pre > code.language-mermaid').forEach((code) => {
    const pre = code.parentElement;
    if (!pre) return;
    const div = document.createElement('div');
    div.className = 'mermaid';
    div.textContent = (code.textContent || '').trim();
    pre.replaceWith(div);
  });
  const nodes = Array.from(root.querySelectorAll<HTMLElement>('.mermaid'));
  if (nodes.length === 0) return;
  await mermaid.run({ nodes });
}

const PRINT_STYLES = `
  @page { margin: 14mm; }
  body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif; line-height: 1.55; color: #111827; font-size: 11pt; margin: 0; }
  main.doc { max-width: 900px; margin: 0 auto; padding: 8px 12px 24px; }
  h1 { font-size: 1.35rem; margin: 1.25rem 0 0.5rem; font-weight: 700; }
  h2 { font-size: 1.15rem; margin: 1.1rem 0 0.45rem; font-weight: 600; }
  h3 { font-size: 1.05rem; margin: 1rem 0 0.35rem; font-weight: 600; }
  h4, h5, h6 { font-size: 1rem; margin: 0.85rem 0 0.3rem; font-weight: 600; }
  p { margin: 0.5rem 0; }
  ul, ol { margin: 0.5rem 0; padding-left: 1.35rem; }
  li { margin: 0.2rem 0; }
  blockquote { margin: 0.75rem 0; padding-left: 0.85rem; border-left: 3px solid #8b5cf6; color: #374151; }
  hr { border: none; border-top: 1px solid #d1d5db; margin: 1rem 0; }
  table { border-collapse: collapse; width: 100%; font-size: 10pt; margin: 0.75rem 0; }
  th, td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f3f4f6; font-weight: 600; }
  pre { background: #f4f4f5; padding: 10px 12px; border-radius: 6px; overflow-x: auto; font-size: 9pt; margin: 0.65rem 0; }
  code { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 0.92em; }
  pre code { font-size: 9pt; }
  a { color: #2563eb; word-break: break-word; }
  .mermaid { margin: 0.75rem 0; text-align: center; }
  .mermaid svg { max-width: 100%; height: auto; display: block; margin: 0 auto; }
  @media print {
    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  }
`;

interface SynergyPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  markdown: string;
  onMarkdownChange: (markdown: string) => void;
  onContinueEditing: () => void;
  onProceedToReconciliation: () => void;
  chat: {
    messages: DraftAiSideChatMessage[];
    input: string;
    sending: boolean;
    disabled?: boolean;
    statusLine?: string;
    onInputChange: (value: string) => void;
    onSend: () => void;
  };
}

export function SynergyPlanDialog({
  open,
  onOpenChange,
  markdown,
  onMarkdownChange,
  onContinueEditing,
  onProceedToReconciliation,
  chat,
}: SynergyPlanDialogProps) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [draft, setDraft] = useState(markdown);
  const dirtyRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      dirtyRef.current = false;
      setMode('preview');
    }
  }, [open]);

  useEffect(() => {
    if (!dirtyRef.current) setDraft(markdown);
  }, [markdown]);

  const persist = useCallback((next: string) => {
    dirtyRef.current = false;
    onMarkdownChange(next);
  }, [onMarkdownChange]);

  const schedulePersist = useCallback((next: string) => {
    dirtyRef.current = true;
    setDraft(next);
    if (saveTimerRef.current != null) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => persist(next), 700);
  }, [persist]);

  useEffect(() => () => {
    if (saveTimerRef.current != null) window.clearTimeout(saveTimerRef.current);
  }, []);

  const handleExportMd = useCallback(() => {
    const stem = synergyPlanFilenameStem();
    downloadTextFile(`${stem}.md`, draft, 'text/markdown;charset=utf-8');
  }, [draft]);

  const handleExportPdf = useCallback(async () => {
    const html = markdownToHtmlForPrint(draft);
    const shell = document.createElement('div');
    shell.setAttribute('data-synergy-pdf-source', 'true');
    // html2canvas often yields a blank PDF for far off-screen or low-opacity roots; keep opacity:1 and
    // shift off-viewport so pixels still rasterize reliably.
    shell.style.cssText = [
      'position:fixed',
      'left:0',
      'top:0',
      'width:794px',
      'max-width:100vw',
      'box-sizing:border-box',
      'transform:translateX(-100%)',
      'webkitTransform:translateX(-100%)',
      'background:#ffffff',
      'color:#111827',
      'z-index:2147483646',
      'pointer-events:none',
      'overflow:visible',
    ].join(';');
    shell.innerHTML = `<style>${PRINT_STYLES}</style><main class="doc">${html}</main>`;
    document.body.appendChild(shell);
    const mainEl = shell.querySelector('main.doc');
    const captureEl = mainEl instanceof HTMLElement ? mainEl : shell;
    setPdfBusy(true);
    try {
      await renderMermaidInPrintRoot(shell);
      await document.fonts.ready.catch(() => undefined);
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );
      const { default: html2pdf } = await import('html2pdf.js');
      const stem = synergyPlanFilenameStem();
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `${stem}.pdf`,
          image: { type: 'jpeg', quality: 0.92 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0,
            windowWidth: captureEl.scrollWidth,
            windowHeight: captureEl.scrollHeight,
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(captureEl)
        .save();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'PDF export failed.');
    } finally {
      shell.remove();
      setPdfBusy(false);
    }
  }, [draft]);

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (!next && dirtyRef.current) persist(draft);
      onOpenChange(next);
    }}>
      <DialogContent maxWidth="full" className="h-[92vh] max-h-[92vh]">
        <DialogHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pr-24">
          <div className="min-w-0 pr-2">
            <DialogTitle>AI Synergy Plan</DialogTitle>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <div className="flex overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant={mode === 'preview' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none gap-1.5"
                onClick={() => {
                  if (dirtyRef.current) persist(draft);
                  setMode('preview');
                }}
              >
                <Eye className="h-4 w-4" />
                Preview
              </Button>
              <Button
                type="button"
                variant={mode === 'edit' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none gap-1.5"
                onClick={() => setMode('edit')}
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handleExportMd}
              title="Download as Markdown"
            >
              <FileDown className="h-4 w-4" />
              Export MD
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => void handleExportPdf()}
              disabled={pdfBusy}
              title="Download as PDF (generated in the browser)"
            >
              {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Export PDF
            </Button>
          </div>
        </DialogHeader>
        <DialogBody className="flex flex-1 min-h-0 overflow-hidden p-0">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {mode === 'edit' ? (
              <textarea
                value={draft}
                onChange={(event) => schedulePersist(event.target.value)}
                onBlur={() => { if (dirtyRef.current) persist(draft); }}
                className="h-full min-h-0 w-full flex-1 resize-none border-0 bg-white px-4 py-3 font-mono text-xs text-gray-900 focus:outline-none dark:bg-gray-900 dark:text-gray-100"
                spellCheck={false}
              />
            ) : (
              <ZoomablePane>
                <div className="px-4 pb-6">
                  <MarkdownPreview content={draft} />
                </div>
              </ZoomablePane>
            )}
          </div>
          <DraftAiSideChat
            messages={chat.messages}
            input={chat.input}
            sending={chat.sending}
            disabled={chat.disabled}
            statusLine={chat.statusLine}
            onInputChange={chat.onInputChange}
            onSend={chat.onSend}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              if (dirtyRef.current) persist(draft);
              onOpenChange(false);
              onContinueEditing();
            }}
          >
            Continue Editing
          </Button>
          <Button
            onClick={() => {
              if (dirtyRef.current) persist(draft);
              onOpenChange(false);
              onProceedToReconciliation();
            }}
          >
            Proceed to Reconciliation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
